import React, { useState, useContext, useRef } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, inpS, btnP, btnSec, sp, rad } from "../../constants/styles";
import Fld from "../common/Fld";
import LoadBtn from "../common/LoadBtn";
import Ic from "../common/Ic";
import useUnsavedWarning from "../../hooks/useUnsavedWarning";
import { uploadPhoto, deletePhoto } from "../../utils/supabaseClient";

const ID_TYPES = [
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "pan", label: "PAN Card" },
  { value: "driving_license", label: "Driving License" },
  { value: "passport", label: "Passport" },
  { value: "voter_id", label: "Voter ID" },
];

function TenantFormPage({ edit }) {
  const { t, h, tens, setTens, props, setProps, leases, setLeases, go, flash } = useContext(Ctx);
  const editId = edit ? localStorage.getItem("editTid") : null;
  const ex = edit ? tens.find(t => t.id === editId) : null;

  // Phone lookup state (for new tenant only)
  const [lookupPhone, setLookupPhone] = useState("");
  const [lookupResult, setLookupResult] = useState(null); // null | { status, tenant? }
  const [lookupLoading, setLookupLoading] = useState(false);
  const [mode, setMode] = useState(edit ? "manual" : null); // null | "manual" | "request_sent"
  const [reqPropertyId, setReqPropertyId] = useState("");
  const [reqDueDay, setReqDueDay] = useState(1);

  const handleLookup = async () => {
    if (!lookupPhone || lookupPhone.length < 10) { flash("Enter a valid 10-digit phone number", "error"); return; }
    setLookupLoading(true);
    try {
      const res = await api.lookupTenant(lookupPhone);
      setLookupResult(res);
    } catch (e) { flash("Lookup failed", "error"); }
    setLookupLoading(false);
  };

  const handleSendRequest = async () => {
    if (!reqPropertyId) { flash("Select a property first", "error"); return; }
    setLookupLoading(true);
    try {
      const res = await api.sendTenantRequest({ phone: lookupPhone, propertyId: reqPropertyId, rentDueDay: reqDueDay });
      if (res.success) { flash("✅ Approval request sent to tenant!"); setMode("request_sent"); }
      else flash(res.message || "Failed to send request", "error");
    } catch (e) { flash("Error sending request", "error"); }
    setLookupLoading(false);
  };

  // Basic fields
  const [name, setN] = useState(ex?.name || "");
  const [ph, setP] = useState(ex?.ph || "");
  const [pass, setPass] = useState(ex?.password || "");
  const [aad, setA] = useState(ex?.aad || "");
  const [pid, setPid] = useState(ex?.pid || "");
  const [email, setEmail] = useState(ex?.email || "");
  const [emergency, setEmergency] = useState(ex?.emergency_contact || "");
  const [rentDueDay, setRentDueDay] = useState(ex?.rent_due_day || 1);

  // Verification fields
  const [idType, setIdType] = useState(ex?.id_type || "aadhaar");
  const [idNumber, setIdNumber] = useState(ex?.id_number || "");
  const [idProofUrl, setIdProofUrl] = useState(ex?.id_proof_url || "");
  const [verified, setVerified] = useState(!!ex?.verified);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useUnsavedWarning(name !== (ex?.name || "") || ph !== (ex?.ph || ""));
  const availProps = props.filter(p => !p.occ || (edit && p.tid === editId));

  const lblS = { color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 };

  const handleIdUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const tid = edit ? editId : `t${Date.now()}`;
      const url = await uploadPhoto(file, `tenant-id/${tid}`);
      setIdProofUrl(url);
      flash("ID proof uploaded!");
    } catch (err) {
      flash(err.message || "Upload failed", "error");
    }
    setUploading(false);
    e.target.value = "";
  };

  const save = async () => {
    if (!name || !ph) { flash(t.namePhoneReq || "Name and phone required", "error"); return; }
    const tid = edit ? editId : `t${Date.now()}`;
    const newTen = {
      id: tid, name, ph, aad, join_date: ex?.join_date || new Date().toISOString().slice(0, 10),
      pid: pid || null, vac: false, password: pass || "123",
      email: email || null, emergency_contact: emergency || null,
      id_type: idType, id_number: idNumber || null, id_proof_url: idProofUrl || null,
      verified: verified ? 1 : 0, rent_due_day: Number(rentDueDay) || 1
    };

    if (edit) setTens(tens.map(t => t.id === tid ? { ...ex, ...newTen } : t)); else setTens([...tens, newTen]);
    await api.saveTenant(newTen);

    // Property assignment logic
    if (pid) {
      if (edit && ex.pid && ex.pid !== pid) {
        const oldP = props.find(p => p.id === ex.pid);
        if (oldP) { setProps(props.map(p => p.id === oldP.id ? { ...oldP, occ: false, tid: null } : p)); api.saveProperty({ ...oldP, occ: false, tid: null }); }
      }
      const newP = props.find(p => p.id === pid);
      if (newP) { setProps(prev => prev.map(p => p.id === newP.id ? { ...newP, occ: true, tid } : p)); api.saveProperty({ ...newP, occ: true, tid }); }
    } else if (edit && ex.pid) {
      const oldP = props.find(p => p.id === ex.pid);
      if (oldP) { setProps(prev => prev.map(p => p.id === oldP.id ? { ...oldP, occ: false, tid: null } : p)); api.saveProperty({ ...oldP, occ: false, tid: null }); }
    }

    // Refresh leases after save (server auto-creates draft lease if property assigned)
    if (pid) {
      try {
        const freshLeases = await api.getLeases();
        if (freshLeases && setLeases) setLeases(freshLeases);
      } catch (e) { /* non-blocking */ }
    }

    flash(edit ? t.tenUpdated : t.tenAdded); go("tens");
  };

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("tens")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{edit ? t.editTenant : t.addTenant}</h2>

    {/* ── Phone Lookup (New Tenant Only) ──────────────────────── */}
    {!edit && !mode && <div style={{ ...crd(h), padding: 20, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Ic n="search" s={16} c={h.ac} />
        <span style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>Find Tenant by Phone</span>
      </div>
      <div style={{ color: h.sc, fontSize: 11, marginBottom: 12 }}>Enter phone number to check if tenant is already registered</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input type="tel" value={lookupPhone} onChange={e => { setLookupPhone(e.target.value.replace(/\D/g, "")); setLookupResult(null); }}
          placeholder="e.g. 9876543210" maxLength={10}
          style={{ ...inpS(h), flex: 1, fontSize: 16, fontWeight: 600 }} />
        <button onClick={handleLookup} disabled={lookupLoading || lookupPhone.length < 10}
          style={{ padding: "10px 16px", borderRadius: 12, border: "none", background: lookupPhone.length >= 10 ? h.ac : h.bd, color: lookupPhone.length >= 10 ? "#fff" : h.sc, cursor: lookupPhone.length >= 10 ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
          {lookupLoading ? "..." : "🔍 Search"}
        </button>
      </div>

      {/* Lookup Results */}
      {lookupResult && lookupResult.status === "already_in_org" && <div style={{ marginTop: 12, padding: 12, background: `${h.wn}10`, borderRadius: 10, border: `1px solid ${h.wn}30` }}>
        <div style={{ color: h.wn, fontWeight: 600, fontSize: 13 }}>⚠️ Already in your organization</div>
        <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{lookupResult.tenant?.name} is already your tenant</div>
      </div>}

      {lookupResult && lookupResult.status === "registered" && <div style={{ marginTop: 12, padding: 14, background: `${h.gn}08`, borderRadius: 12, border: `1px solid ${h.gn}25` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 18, background: `${h.gn}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ic n="user-check" s={18} c={h.gn} />
          </div>
          <div>
            <div style={{ color: h.gn, fontWeight: 600, fontSize: 13 }}>✓ {lookupResult.tenant?.name} is registered</div>
            <div style={{ color: h.sc, fontSize: 11 }}>{lookupResult.tenant?.verified ? "✓ ID Verified" : "Not verified yet"}</div>
          </div>
        </div>
        <div style={{ color: h.sc, fontSize: 11, marginBottom: 10 }}>Send an approval request — the tenant will see it in their app and can approve to share their profile with you.</div>

        <label style={{ color: h.sc, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase" }}>Assign Property</label>
        <select value={reqPropertyId} onChange={e => setReqPropertyId(e.target.value)} style={{ ...inpS(h), cursor: "pointer", marginBottom: 8 }}>
          <option value="">-- Select Property --</option>
          {availProps.map(p => <option key={p.id} value={p.id}>{p.addr} — ₹{p.rent?.toLocaleString()}/mo</option>)}
        </select>

        <label style={{ color: h.sc, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase" }}>Rent Due Day</label>
        <select value={reqDueDay} onChange={e => setReqDueDay(Number(e.target.value))} style={{ ...inpS(h), cursor: "pointer", marginBottom: 12 }}>
          {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d === 1 ? "1st" : d === 2 ? "2nd" : d === 3 ? "3rd" : `${d}th`} of every month</option>)}
        </select>

        <button onClick={handleSendRequest} disabled={lookupLoading} style={{ width: "100%", padding: 12, borderRadius: 10, border: "none", background: h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          {lookupLoading ? "Sending..." : "📩 Send Approval Request"}
        </button>
      </div>}

      {lookupResult && lookupResult.status === "pending_request" && <div style={{ marginTop: 12, padding: 12, background: `${h.wn}10`, borderRadius: 10, border: `1px solid ${h.wn}30` }}>
        <div style={{ color: h.wn, fontWeight: 600, fontSize: 13 }}>⏳ Request Already Sent</div>
        <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>Waiting for tenant to approve. They'll see it in their notifications.</div>
      </div>}

      {lookupResult && lookupResult.status === "not_found" && <div style={{ marginTop: 12, padding: 12, background: `${h.bd}40`, borderRadius: 10, border: `1px solid ${h.bd}` }}>
        <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>Not registered yet</div>
        <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>This phone is not registered as a tenant. You can create their profile manually.</div>
        <button onClick={() => { setMode("manual"); setP(lookupPhone); }} style={{ marginTop: 10, padding: "8px 16px", borderRadius: 8, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          ✏️ Create Manually
        </button>
      </div>}

      <div style={{ textAlign: "center", marginTop: 12 }}>
        <button onClick={() => setMode("manual")} style={{ background: "none", border: "none", color: h.sc, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>
          Skip lookup — Add tenant manually
        </button>
      </div>
    </div>}

    {/* Request Sent Confirmation */}
    {mode === "request_sent" && <div style={{ ...crd(h), padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>📩</div>
      <div style={{ color: h.gn, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Request Sent!</div>
      <div style={{ color: h.sc, fontSize: 12, lineHeight: 1.5 }}>
        The tenant will receive a notification in their app.<br/>
        When they approve, their profile will be automatically linked to your property.
      </div>
      <button onClick={() => go("tens")} style={{ marginTop: 16, padding: "10px 24px", borderRadius: 10, border: "none", background: h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
        ← Back to Tenants
      </button>
    </div>}

    {/* ── Manual Form (edit mode OR selected manual) ─────────── */}
    {(edit || mode === "manual") && <>

    {/* Basic Info */}
    <div style={{ ...crd(h), padding: 20 }}>
      <Fld l={t.name} v={name} set={setN} h={h} />
      <Fld l={t.phone} v={ph} set={setP} h={h} ph="+91..." />
      <Fld l={t.pwdLogin} v={pass} set={setPass} h={h} ph="e.g. 123456" />
      <Fld l={t.email || "Email"} v={email} set={setEmail} h={h} ph="tenant@email.com" />
      <Fld l={t.emergencyContact || "Emergency Contact"} v={emergency} set={setEmergency} h={h} ph="+91..." />

      <label style={lblS}>{t.assign} {t.property}</label>
      <select value={pid} onChange={e => setPid(e.target.value)} style={{ ...inpS(h), cursor: "pointer" }}>
        <option value="">-- {t.none} --</option>
        {availProps.map(p => <option key={p.id} value={p.id}>{p.addr}</option>)}
      </select>

      {/* Rent Due Day */}
      <label style={{ ...lblS, marginTop: 14 }}>{"📅"} Rent Due Day (Monthly)</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <select value={rentDueDay} onChange={e => setRentDueDay(Number(e.target.value))} style={{ ...inpS(h), cursor: "pointer", flex: 1 }}>
          {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
            <option key={d} value={d}>{d === 1 ? "1st" : d === 2 ? "2nd" : d === 3 ? "3rd" : `${d}th`} of every month</option>
          ))}
        </select>
      </div>
      <div style={{ color: h.sc, fontSize: 10, marginTop: 4 }}>Rent will be auto-generated on this day each month</div>
    </div>

    {/* ID Verification Section */}
    <div style={{ ...crd(h), padding: 20, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.md }}>
        <Ic n="shield" s={16} c={h.ac} />
        <span style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{t.idVerification || "ID Verification"}</span>
      </div>

      {/* ID Type */}
      <label style={lblS}>{t.idType || "ID Type"}</label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: sp.md }}>
        {ID_TYPES.map(id => (
          <button key={id.value} onClick={() => setIdType(id.value)}
            style={{ padding: "6px 12px", borderRadius: 20, border: `1px solid ${idType === id.value ? h.ac : h.bd}`, background: idType === id.value ? `${h.ac}15` : "transparent", color: idType === id.value ? h.ac : h.sc, cursor: "pointer", fontSize: 11, fontWeight: idType === id.value ? 600 : 400 }}>
            {id.label}
          </button>
        ))}
      </div>

      {/* ID Number */}
      <Fld l={t.idNumber || `${ID_TYPES.find(i => i.value === idType)?.label} Number`} v={idNumber} set={setIdNumber} h={h} ph={idType === "aadhaar" ? "XXXX XXXX XXXX" : idType === "pan" ? "ABCDE1234F" : "Enter ID number"} />

      {/* Aadhaar last 4 (legacy field) */}
      {idType === "aadhaar" && <Fld l={t.aadhaar || "Aadhaar Last 4"} v={aad} set={setA} h={h} ph="xxxx" type="number" />}

      {/* ID Proof Upload */}
      <label style={lblS}>{t.idProof || "ID Proof Document"}</label>
      {idProofUrl ? (
        <div style={{ position: "relative", marginBottom: sp.md }}>
          <img src={idProofUrl} alt="ID Proof" style={{ width: "100%", maxHeight: 200, objectFit: "contain", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib }} />
          <button onClick={() => { deletePhoto(idProofUrl); setIdProofUrl(""); }}
            style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: 12, background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>{"\u00D7"}</button>
          <div style={{ display: "flex", alignItems: "center", gap: sp.xs, marginTop: sp.xs }}>
            <Ic n="check-circle" s={12} c={h.gn} />
            <span style={{ color: h.gn, fontSize: 11 }}>ID proof uploaded</span>
          </div>
        </div>
      ) : (
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ width: "100%", padding: sp.lg, borderRadius: rad.md, border: `2px dashed ${h.bd}`, background: h.ib, cursor: uploading ? "default" : "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: sp.sm, marginBottom: sp.md }}>
          {uploading ? <span style={{ color: h.ts, fontSize: 12 }}>Uploading...</span> : <>
            <Ic n="plus" s={20} c={h.ts} />
            <span style={{ color: h.ts, fontSize: 12 }}>Upload ID Proof Photo</span>
            <span style={{ color: h.tt, fontSize: 10 }}>Aadhaar, PAN, Driving License, etc.</span>
          </>}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleIdUpload} />

      {/* Verification Toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${sp.md}px 0`, borderTop: `1px solid ${h.bd}` }}>
        <div>
          <div style={{ color: h.tx, fontSize: 13, fontWeight: 500 }}>{t.markVerified || "Mark as Verified"}</div>
          <div style={{ color: h.tt, fontSize: 10 }}>Confirm tenant identity is checked</div>
        </div>
        <button onClick={() => setVerified(!verified)} style={{
          width: 48, height: 28, borderRadius: 14, border: "none", cursor: "pointer",
          background: verified ? h.gn : `${h.sc}40`, position: "relative", transition: "background 0.2s"
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: 11, background: "#fff",
            position: "absolute", top: 3, left: verified ? 23 : 3, transition: "left 0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
          }} />
        </button>
      </div>
      {verified && <div style={{ display: "flex", alignItems: "center", gap: sp.xs, background: `${h.gn}10`, padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm }}>
        <Ic n="check-circle" s={14} c={h.gn} />
        <span style={{ color: h.gn, fontSize: 11, fontWeight: 600 }}>Tenant will be marked as verified</span>
      </div>}
    </div>

    {/* Actions */}
    <div style={{ ...crd(h), padding: 20, marginTop: 12 }}>
      <div style={{ display: "flex", gap: 10 }}>
        <LoadBtn onClick={save} label={t.save} style={{ ...btnP(h), flex: 1 }} />
        <button onClick={() => go("tens")} style={{ ...btnSec(h), flex: 1 }}>{t.cancel}</button>
      </div>
    </div>

    </>}
  </div>;
}

export default TenantFormPage;
